import { memo, useEffect, useRef, useState } from "react";
import { react, useChanged } from "uikit";
import PropsOf from "uikit/src/types/PropsOf";
import { useMap } from "react-leaflet";
import Color from "color";
import {
	divIcon,
	layerGroup,
	marker as createLeafletMarker,
	LatLng,
	LayerGroup,
	Marker as LeafletMarker,
} from "leaflet";

import { Executor, ExecutorLocations } from "../../services";
import { ExecutorStatus } from "../../services/Executor";
import OrderSource from "../../types/OrderSource";
import { store, useTypedSelector } from "../../redux/store";

import LocalController from "./Controller";
import { executorTooltipHtml } from "./tooltipHtml";
import {
	clusterExecutorPoints,
	filterLeavesInBounds,
	ClusterLeaf,
	CLUSTER_MAX_ZOOM,
	VIEWPORT_PAD,
} from "./cluster";
import "./markers.css";
import { gpsStaleTickMsFromModels, isExecutorMapGpsFresh } from "./gpsFresh.util";

type MarkerWithLoc = LeafletMarker & {
	__loc?: ExecutorLocations.Model;
	__clusterCallSigns?: string[];
};

const executorStatusToColor = {
	[ExecutorStatus.AVAILABLE]: "#4baf4f",
	[ExecutorStatus.BUSY]: "#ff9d48",
	[ExecutorStatus.DINNER]: "#40a4f4",
	[ExecutorStatus.HOME]: "#3d50b2",
	[ExecutorStatus.CLOSED]: "#7b3636",
};

const lightenCache = new Map<string, string>();
const lighten = (hex: string) => {
	const cached = lightenCache.get(hex);
	if (cached) return cached;
	const next = Color(hex).lighten(0.25).hex();
	lightenCache.set(hex, next);
	return next;
};

const lightCache = new Map<string, boolean>();
const isLightHex = (hex: string) => {
	const cached = lightCache.get(hex);
	if (cached !== undefined) return cached;
	const next = Color(hex).isLight();
	lightCache.set(hex, next);
	return next;
};

const escapeHtml = (value: string) =>
	value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");

const DARK_ICON = "#21333F";
const LIGHT_ICON = "#FFFFFF";
const CAR_PATH =
	"M13.4476 4.12769H12.5673L12.1272 2.25718C11.8796 1.2119 10.9719 0.496704 9.9266 0.496704H4.20505C3.13226 0.496704 2.22452 1.2119 2.00446 2.25718L1.56434 4.12769H0.684101C0.354012 4.12769 0.106445 4.37525 0.106445 4.70534V5.44804C0.106445 5.75063 0.326505 5.99819 0.601579 6.0257L1.06921 6.08071C1.01419 6.27327 0.986683 6.46582 0.986683 6.65837V8.00623V8.83146V10.6194C0.986683 10.9495 1.23425 11.1971 1.56434 11.1971H3.37983C3.6549 11.1971 3.90247 11.0045 3.95749 10.7295L4.20505 9.40912H9.89909L10.1467 10.7295C10.2017 11.0045 10.4492 11.1971 10.7243 11.1971H12.5398C12.8699 11.1971 13.1175 10.9495 13.1175 10.6194V8.83146V8.00623V6.65837C13.1175 6.46582 13.09 6.24576 13.0349 6.08071L13.5026 6.0257C13.7776 5.99819 13.9977 5.75063 13.9977 5.44804V4.70534C14.0252 4.37525 13.7776 4.12769 13.4476 4.12769ZM3.29731 2.55976C3.40734 2.14715 3.76493 1.84457 4.20505 1.84457H9.9266C10.3667 1.84457 10.7243 2.14715 10.8343 2.55976L11.3295 4.70534H2.80217L3.29731 2.55976ZM4.17755 7.78618L2.30704 7.70365C2.142 7.70365 2.03197 7.56612 2.00446 7.42858C1.97695 7.18101 1.97695 6.82342 2.03197 6.52083C2.05947 6.32828 2.25203 6.21825 2.41707 6.27327C2.99473 6.43831 3.79244 6.65837 4.15004 6.74089C4.26007 6.7684 4.34259 6.85092 4.3701 6.96095L4.48013 7.40107C4.53514 7.62113 4.3701 7.81368 4.17755 7.78618ZM8.55123 7.48359C8.55123 7.59362 8.46871 7.67615 8.35868 7.67615H5.74547C5.63544 7.67615 5.55292 7.59362 5.55292 7.48359V7.126C5.55292 7.01597 5.63544 6.93345 5.74547 6.93345H8.35868C8.46871 6.93345 8.55123 7.01597 8.55123 7.126V7.48359ZM12.1272 7.42858C12.1272 7.59362 11.9897 7.70365 11.8246 7.70365L9.95411 7.78618C9.76156 7.78618 9.59651 7.59362 9.65153 7.40107L9.76156 6.96095C9.78906 6.85092 9.87159 6.7684 9.98162 6.74089C10.3392 6.65837 11.1369 6.43831 11.7146 6.27327C11.8796 6.21825 12.0722 6.32828 12.0997 6.52083C12.1547 6.82342 12.1272 7.18101 12.1272 7.42858Z";

const isOwnOrder = (executor?: ExecutorLocations.Model["executor"]) =>
	Boolean(
		executor?.orders?.some(
			(order) =>
				order.source === OrderSource.EXECUTOR &&
				order.status !== "closed",
		),
	);

const colorsForExecutor = (executor?: ExecutorLocations.Model["executor"]) => {
	const onOrder = Boolean(executor?.hasActiveOrder);
	let onOrderStatus: "ownOrder" | "otherOrder" | null = null;

	if (onOrder) {
		onOrderStatus = isOwnOrder(executor) ? "ownOrder" : "otherOrder";
	}

	let mainColor = "#a0a0a0";
	let secondaryColor = "#fff509";

	if (!executor?.isWorking || onOrder) {
		secondaryColor =
			executorStatusToColor[
				executor?.status as keyof typeof executorStatusToColor
			] ?? "#fff509";

		if (!executor?.isWorking) {
			mainColor = "#a0a0a0";
			secondaryColor = lighten(secondaryColor);
		} else {
			mainColor = onOrderStatus === "ownOrder" ? "#654cad" : "#e5503b";
		}
	} else {
		mainColor =
			executorStatusToColor[
				executor.status as keyof typeof executorStatusToColor
			] ?? "#a0a0a0";
		secondaryColor = "#fff509";
	}

	return { mainColor, secondaryColor };
};

const carSvg = (fill: string) =>
	`<svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 14 12" width="20" height="20" tabindex="-1" style="--icon-color-1:${fill}"><path d="${CAR_PATH}" fill="var(--icon-color-1)"/></svg>`;

const carIconHtml = ({
	alias,
	mainColor,
	secondaryColor,
	focused,
	dimmed,
	gpsStale,
}: {
	alias: string;
	mainColor: string;
	secondaryColor: string;
	focused: boolean;
	dimmed: boolean;
	gpsStale: boolean;
}) => {
	const iconFill = isLightHex(secondaryColor) ? DARK_ICON : LIGHT_ICON;
	const rootClass = `exec-car-root${focused ? " is-focused" : ""}${
		dimmed ? " is-dimmed" : ""
	}${gpsStale ? " is-gps-stale" : ""}`;
	return `<div style="zoom:100%;overflow:visible"><div class="${rootClass}"><div class="exec-car-overlayer"><div class="exec-car-overlayer-content"></div><div class="exec-car-overlayer-overlay"><div class="exec-car-body" style="--pin:${mainColor}"><div class="exec-car-overlayer"><div class="exec-car-overlayer-content"><div class="exec-car-alias">${escapeHtml(
		alias,
	)}</div></div><div class="exec-car-overlayer-overlay"><div class="exec-car-badge" style="--badge:${secondaryColor}">${carSvg(
		iconFill,
	)}</div></div></div></div></div></div></div></div>`;
};

const clusterIconHtml = (count: number) =>
	`<div class="exec-cluster-root"><div class="exec-cluster-circle">${count}</div></div>`;

const clusterTooltipHtml = (callSigns: string[]) => {
	const cells =
		callSigns.length > 0
			? callSigns
					.map(
						(sign) =>
							`<span class="exec-cluster-callsign">${escapeHtml(
								sign,
							)}</span>`,
					)
					.join("")
			: `<span class="exec-cluster-callsign">—</span>`;
	return `<div class="exec-cluster-tooltip">${cells}</div>`;
};

const styleSigFor = (
	location: ExecutorLocations.Model,
	focused: boolean,
	dimmed: boolean,
	gpsStale: boolean,
) =>
	`${location.executor?.alias ?? ""}|${location.executor?.status ?? ""}|${
		location.executor?.online ? 1 : 0
	}|${location.executor?.isWorking ? 1 : 0}|${
		location.executor?.hasActiveOrder ? 1 : 0
	}|${isOwnOrder(location.executor) ? 1 : 0}|${focused ? 1 : 0}|${
		dimmed ? 1 : 0
	}|${gpsStale ? 1 : 0}`;

const makeIcon = (html: string, size: [number, number] = [100, 50]) =>
	divIcon({
		className: "dummy",
		iconSize: size,
		html,
	});

const iconForLocation = (
	location: ExecutorLocations.Model,
	focused: boolean,
	dimmed: boolean,
	gpsStale: boolean,
) => {
	const { mainColor, secondaryColor } = colorsForExecutor(location.executor);
	return makeIcon(
		carIconHtml({
			alias: location.executor?.alias ?? "",
			mainColor,
			secondaryColor,
			focused,
			dimmed,
			gpsStale,
		}),
	);
};

const attachCarTooltip = (marker: MarkerWithLoc) => {
	marker.bindTooltip("", {
		direction: "bottom",
		opacity: 0.9,
	});
	marker.on("tooltipopen", () => {
		const loc = marker.__loc;
		const html = loc ? executorTooltipHtml(loc) : "";
		if (!html) {
			marker.closeTooltip();
			return;
		}
		marker.setTooltipContent(html);
	});
};

const attachClusterTooltip = (marker: MarkerWithLoc) => {
	marker.bindTooltip("", {
		direction: "top",
		opacity: 0.95,
		sticky: true,
		className: "exec-cluster-tooltip-pane",
	});
	marker.on("tooltipopen", () => {
		const signs = marker.__clusterCallSigns || [];
		marker.setTooltipContent(clusterTooltipHtml(signs));
	});
};

type MarkerMeta = {
	styleSig: string;
	lat: number;
	lng: number;
	kind: "leaf" | "cluster";
};

const MapExecutorLocationsBase = react.withController<
	MapExecutorLocations.PropsBase,
	LocalController
>(({ controller, executorLocations, getLocations, highlightedExecutorId }) => {
	const map = useMap();

	const [focusedExecutorId, setFocusedExecutorId] = useState<
		number | undefined
	>(undefined);

	const highlightId = highlightedExecutorId ?? focusedExecutorId;
	const dimOthers = Boolean(highlightedExecutorId);

	const groupRef = useRef<LayerGroup | null>(null);
	const markersRef = useRef<Map<string, MarkerWithLoc>>(new Map());
	const metaRef = useRef<Map<string, MarkerMeta>>(new Map());
	const pendingRef = useRef<ExecutorLocations.Model[]>(
		executorLocations || [],
	);
	const highlightRef = useRef({
		highlightId,
		dimOthers,
		highlightedExecutorId,
	});
	const rafRef = useRef(0);
	const getLocationsRef = useRef(getLocations);
	const syncMarkersRef = useRef<() => void>(() => {});
	getLocationsRef.current = getLocations;

	highlightRef.current = { highlightId, dimOthers, highlightedExecutorId };
	if (executorLocations) pendingRef.current = executorLocations;

	const scheduleSync = () => {
		if (rafRef.current) return;
		rafRef.current = requestAnimationFrame(() => {
			rafRef.current = 0;
			syncMarkersRef.current();
		});
	};

	syncMarkersRef.current = () => {
		const group = groupRef.current;
		if (!group) return;

		const locations = pendingRef.current || [];
		const {
			highlightId: focusId,
			dimOthers: dim,
			highlightedExecutorId: hi,
		} = highlightRef.current;

		const leaves: ClusterLeaf[] = [];
		for (let i = 0; i < locations.length; i += 1) {
			const location = locations[i];
			const id = location.executor?.id ?? location.executorId;
			const lat = location.point?.lat;
			const lng = location.point?.lng;
			if (id && Number.isFinite(lat) && Number.isFinite(lng)) {
				leaves.push({
					id,
					lat,
					lng,
					callSign:
						location.executor?.alias ||
						location.executorCallSign ||
						String(id),
					location,
				});
			}
		}

		const bounds = map.getBounds().pad(VIEWPORT_PAD);
		let visible = filterLeavesInBounds(
			leaves,
			bounds.getWest(),
			bounds.getSouth(),
			bounds.getEast(),
			bounds.getNorth(),
		);
		if (focusId) {
			const focusedVisible = visible.some((leaf) => leaf.id === focusId);
			if (!focusedVisible) {
				const focused = leaves.find((leaf) => leaf.id === focusId);
				if (focused) visible = visible.concat(focused);
			}
		}
		const nodes = clusterExecutorPoints(visible, CLUSTER_MAX_ZOOM);
		const nextKeys = new Set<string>();

		for (let i = 0; i < nodes.length; i += 1) {
			const node = nodes[i];
			nextKeys.add(node.key);
			const prev = metaRef.current.get(node.key);
			const existing = markersRef.current.get(node.key);

			if (node.kind === "cluster") {
				const styleSig = `cluster|${node.count}|${node.callSigns.join(
					",",
				)}`;
				if (!existing) {
					const marker = createLeafletMarker(
						new LatLng(node.lat, node.lng),
						{
							icon: makeIcon(
								clusterIconHtml(node.count),
								[44, 44],
							),
							keyboard: false,
						},
					) as MarkerWithLoc;
					marker.__clusterCallSigns = node.callSigns;
					attachClusterTooltip(marker);
					marker.on("click", () => {
						map.setView([node.lat, node.lng], CLUSTER_MAX_ZOOM, {
							animate: true,
						});
					});
					marker.addTo(group);
					markersRef.current.set(node.key, marker);
				} else {
					existing.__clusterCallSigns = node.callSigns;
					if (
						!prev ||
						prev.lat !== node.lat ||
						prev.lng !== node.lng
					) {
						existing.setLatLng([node.lat, node.lng]);
					}
					if (!prev || prev.styleSig !== styleSig) {
						existing.setIcon(
							makeIcon(clusterIconHtml(node.count), [44, 44]),
						);
					}
					const tip = existing.getTooltip();
					if (tip?.isOpen()) {
						tip.setContent(clusterTooltipHtml(node.callSigns));
					}
				}
				metaRef.current.set(node.key, {
					styleSig,
					lat: node.lat,
					lng: node.lng,
					kind: "cluster",
				});
			} else {
				const { leaf } = node;
				const { location } = leaf;
				const focused = location.executor?.id === focusId;
				const dimmed = dim && location.executor?.id !== hi;
				// Свежесть как у GPS-шага раздачи; тик ниже только scheduleSync, без setState
				const gpsStale = !isExecutorMapGpsFresh(location);
				const styleSig = styleSigFor(location, focused, dimmed, gpsStale);

				if (!existing) {
					const marker = createLeafletMarker(
						new LatLng(node.lat, node.lng),
						{
							icon: iconForLocation(
								location,
								focused,
								dimmed,
								gpsStale,
							),
							keyboard: false,
						},
					) as MarkerWithLoc;
					marker.__loc = location;
					attachCarTooltip(marker);
					marker.addTo(group);
					markersRef.current.set(node.key, marker);
				} else {
					existing.__loc = location;
					if (
						!prev ||
						prev.lat !== node.lat ||
						prev.lng !== node.lng
					) {
						existing.setLatLng([node.lat, node.lng]);
					}
					if (
						!prev ||
						prev.styleSig !== styleSig ||
						prev.kind !== "leaf"
					) {
						existing.setIcon(
							iconForLocation(
								location,
								focused,
								dimmed,
								gpsStale,
							),
						);
					}
					const tip = existing.getTooltip();
					if (tip?.isOpen()) {
						const html = executorTooltipHtml(location);
						if (html) tip.setContent(html);
					}
				}

				metaRef.current.set(node.key, {
					styleSig,
					lat: node.lat,
					lng: node.lng,
					kind: "leaf",
				});
			}
		}

		const stale: string[] = [];
		markersRef.current.forEach((_, key) => {
			if (!nextKeys.has(key)) stale.push(key);
		});
		for (let i = 0; i < stale.length; i += 1) {
			const key = stale[i];
			const staleMarker = markersRef.current.get(key);
			if (staleMarker) group.removeLayer(staleMarker);
			markersRef.current.delete(key);
			metaRef.current.delete(key);
		}
	};

	useEffect(() => {
		const group = layerGroup().addTo(map);
		const markers = markersRef.current;
		const meta = metaRef.current;
		groupRef.current = group;
		scheduleSync();

		const onViewChange = () => scheduleSync();
		map.on("zoomend", onViewChange);
		map.on("moveend", onViewChange);

		return () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
			rafRef.current = 0;
			map.off("zoomend", onViewChange);
			map.off("moveend", onViewChange);
			group.remove();
			groupRef.current = null;
			markers.clear();
			meta.clear();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map]);

	useEffect(() => {
		if (executorLocations || !getLocations) return undefined;

		let prevLoc: unknown;
		let prevCars: unknown;
		const pull = () => {
			const state = store.getState();
			const loc =
				state.globalState.executorLocations.data.subscription.basic;
			const { cars } = state.orders.map.data;
			if (loc === prevLoc && cars === prevCars) return;
			prevLoc = loc;
			prevCars = cars;
			pendingRef.current = getLocationsRef.current?.() || [];
			scheduleSync();
		};

		pull();
		return store.subscribe(pull);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [executorLocations, getLocations]);

	useEffect(() => {
		if (executorLocations) pendingRef.current = executorLocations;
		scheduleSync();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [executorLocations, highlightId, dimOthers, highlightedExecutorId]);

	// Пересчёт opacity когда сокет молчит, а TTL уже вышел. Не setState — только Leaflet.
	const gpsTickMs = useTypedSelector((state) =>
		gpsStaleTickMsFromModels(state.taxiServices?.models || []),
	);
	useEffect(() => {
		const id = window.setInterval(() => {
			scheduleSync();
		}, gpsTickMs);
		return () => window.clearInterval(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [gpsTickMs]);

	controller.setContext({
		setFocusedExecutorId,
		map,
		focusedExecutorId,
		getExecutorLocations: () => pendingRef.current || [],
	});

	useChanged(() => {
		controller.focus();
	}, focusedExecutorId);

	return null;
}, LocalController);

const MapExecutorLocations = memo(MapExecutorLocationsBase);

declare namespace MapExecutorLocations {
	type Controller = LocalController;

	interface PropsBase {
		executorLocations?: ExecutorLocations.Model[];
		getLocations?: () => ExecutorLocations.Model[];
		executors?: Executor.Model[];
		highlightedExecutorId?: number;
	}

	type Props = PropsOf<typeof MapExecutorLocations>;
}

export default MapExecutorLocations;
